import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ExternalLink, ChevronLeft, ChevronRight, Mail, Phone, MapPin, Users, CheckCircle } from 'lucide-react';
import { settingsService } from '../services/settings';
import { structuresService } from '../services/structures';
import { Login } from '../components/Login';
import { StructureWithStats } from '../types';
import { sanitizeHtml, sanitizeUrl } from '../utils/sanitize';

interface Section {
  title: string;
  imageUrl: string;
  content: string;
  trainingUrl: string;
}

interface LandingContent {
  title: string;
  subtitle: string;
  backgroundImage: string;
  bannerImages: string[];
  section1: Section;
  section2: Section;
  section3: Section;
  section4: Section;
  menuItems: {
    id: string;
    label: string;
    url: string;
    target: string;
  }[];
  useFormasaasStyle: boolean;
  visibleSections: {
    menu: boolean;
    header: boolean;
    footer: boolean;
    backgroundImage: boolean;
    banner: boolean;
    section1: boolean;
    section2: boolean;
    section3: boolean;
    section4: boolean;
    search: boolean;
  };
  footer: {
    address: string;
    phone: string;
    mobile: string;
    email: string;
  };
}

const SectionComponent: React.FC<{
  section: Section;
  visible: boolean;
}> = ({ section, visible }) => {
  if (!visible) return null;

  return (
    <section className="bg-white rounded-xl p-8 w-full shadow-[0_20px_50px_rgba(0,0,0,0.3)] transition-shadow hover:shadow-[0_25px_60px_rgba(0,0,0,0.4)]">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">{section.title}</h2>
      <div className="grid md:grid-cols-2 gap-8">
        <div className="relative h-[300px] overflow-hidden rounded-lg">
          <img 
            src={section.imageUrl}
            alt={section.title}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative min-h-[300px]">
          <div
            className="prose max-w-none mb-16"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(section.content) }}
          />
          {sanitizeUrl(section.trainingUrl) && (
          <a
            href={sanitizeUrl(section.trainingUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-0 right-0 inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          >
            Accéder à la formation
            <ExternalLink className="w-4 h-4" />
          </a>
          )}
        </div>
      </div>
    </section>
  );
};

// Contenu par défaut pour affichage immédiat
const defaultContent: LandingContent = {
  title: 'Bienvenue chez CQO',
  subtitle: 'Retrouvez ci-dessous nos formations dédiées à nos applications',
  backgroundImage: '',
  bannerImages: [
    'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1170&h=450&fit=crop',
    'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1170&h=450&fit=crop'
  ],
  menuItems: [
    { id: '1', label: 'A propos de CQO', url: 'https://www.bycqo.com', target: '_blank' },
    { id: '2', label: 'Contactez nous', url: 'https://www.bycqo.com/contact', target: '_blank' }
  ],
  footer: {
    address: '12 rue du 35ème régiment d\'aviation - 69500, Bron',
    phone: '+33 (0)4 27 11 85 26',
    mobile: '06 08 61 40 91',
    email: 'formalog@cqo-dentaire.fr'
  },
  section1: { title: '', imageUrl: '', content: '', trainingUrl: '' },
  section2: { title: '', imageUrl: '', content: '', trainingUrl: '' },
  section3: { title: '', imageUrl: '', content: '', trainingUrl: '' },
  section4: { title: '', imageUrl: '', content: '', trainingUrl: '' },
  useFormasaasStyle: false,
  visibleSections: {
    menu: true,
    header: true,
    footer: true,
    backgroundImage: true,
    banner: true,
    section1: false,
    section2: false,
    section3: false,
    section4: false,
    search: true
  }
};

export const Landing: React.FC = () => {
  const [clientNumber, setClientNumber] = useState('');
  const [structureStats, setStructureStats] = useState<StructureWithStats | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Initialiser avec le contenu par défaut pour affichage immédiat
  const [content, setContent] = useState<LandingContent>(defaultContent);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const loadContent = async () => {
      try {
        // Timeout de 3 secondes pour le chargement du contenu
        const timeoutPromise = new Promise<null>((resolve) => {
          setTimeout(() => resolve(null), 3000);
        });

        const contentPromise = settingsService.getLandingContent();
        const data = await Promise.race([contentPromise, timeoutPromise]);

        console.log('Landing content loaded:', data);
        console.log('Visible sections:', data?.visibleSections);

        if (data) {
          setContent(data);
        }
      } catch (error) {
        console.error('Error loading landing content:', error);
        // Garde le contenu par défaut en cas d'erreur
      }
    };
    loadContent();
  }, []);

  useEffect(() => {
    if (content?.bannerImages.length && content.visibleSections.banner) {
      const timer = setInterval(() => {
        setCurrentImageIndex((current) => 
          current === content.bannerImages.length - 1 ? 0 : current + 1
        );
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [content?.bannerImages.length, content?.visibleSections.banner]);

  const handleSearch = async () => {
    if (!clientNumber.trim()) return;

    setIsSearching(true);
    setSearchError(null);
    setStructureStats(null);

    try {
      // Search for structure by external client ID
      const structure = await structuresService.searchByClientNumber(clientNumber.trim());

      if (structure) {
        setStructureStats(structure);
      } else {
        setSearchError('Aucune structure trouvée pour ce numéro client');
      }
    } catch (error) {
      console.error('Error searching structure:', error);
      setSearchError('Une erreur est survenue lors de la recherche');
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const nextImage = () => {
    if (content?.bannerImages.length) {
      setCurrentImageIndex((current) => 
        current === content.bannerImages.length - 1 ? 0 : current + 1
      );
    }
  };

  const prevImage = () => {
    if (content?.bannerImages.length) {
      setCurrentImageIndex((current) => 
        current === 0 ? content.bannerImages.length - 1 : current - 1
      );
    }
  };

  // Mode FormaSaas
  if (content.useFormasaasStyle) {
    return (
      <div className="min-h-screen relative flex flex-col bg-white">
        {/* Hex Pattern Background */}
        <svg className="fixed inset-0 w-full h-full opacity-5 z-0" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hex-landing" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform="scale(1.5)">
              <path d="M28 66L0 50L0 16L28 0L56 16L56 50L28 66Z" fill="none" stroke="#004B87" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hex-landing)"/>
        </svg>

        {/* Header FormaSaas */}
        <header className="bg-white shadow-md relative z-10">
          <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <a href="/" className="flex items-center">
              <img src="/images/logo-formasaas.png" alt="FormaSaas" className="h-[50px]" />
            </a>
            <div className="flex items-center gap-4">
              <a
                href="https://formasaas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 text-white font-semibold rounded-lg transition-all hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: '#F39200' }}
              >
                Accueil FormaSaas
              </a>
              <a
                href="https://cours.formasaas.com"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2.5 text-white font-semibold rounded-lg transition-all hover:shadow-lg hover:-translate-y-0.5"
                style={{ backgroundColor: '#004B87' }}
              >
                Cours en ligne
              </a>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <div className="relative z-10 flex-grow flex items-center justify-center py-16">
          <div className="w-full max-w-md mx-auto px-4">
            {/* Titre au-dessus du bloc de connexion */}
            <h1 className="text-center mb-8">
              <span className="text-3xl md:text-4xl font-bold" style={{ color: '#004B87' }}>
                Accédez à votre
              </span>
              <br />
              <span className="text-3xl md:text-4xl font-bold" style={{ color: '#009FDA' }}>
                Tableau de bord
              </span>
            </h1>

            {/* Bloc de connexion */}
            <section id="connexion" className="bg-white rounded-xl p-8 shadow-[0_20px_50px_rgba(0,75,135,0.15)] border border-gray-100">
              <Login onLoginSuccess={() => navigate('/dashboard')} />
            </section>
          </div>
        </div>

        {/* Footer minimaliste */}
        <footer className="bg-white border-t border-gray-100 relative z-10 py-4">
          <div className="max-w-7xl mx-auto px-6 text-center">
            <p className="text-slate-500 text-sm">© 2025 FormaSaas. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    );
  }

  // Mode classique (configuration manuelle)
  return (
    <div className="min-h-screen relative flex flex-col">
      {/* Fixed background with reduced opacity */}
      {content.visibleSections.backgroundImage && content.backgroundImage && (
        <div
          className="fixed inset-0 z-0 opacity-15"
          style={{
            backgroundImage: `url(${content.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
      )}

      {/* Top Menu Bar */}
      {content.visibleSections.menu && (
        <div className="bg-white shadow-md relative z-10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex-1"></div>
              <nav className="flex items-center gap-6">
                {content.menuItems.map((item) => {
                  const safeUrl = sanitizeUrl(item.url);
                  if (!safeUrl) return null;
                  return (
                  <a
                    key={item.id}
                    href={safeUrl}
                    target={item.target}
                    rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                    className="text-primary hover:text-primary/80 font-medium flex items-center gap-1"
                  >
                    {item.label}
                    {item.target === '_blank' && <ExternalLink className="w-3 h-3" />}
                  </a>
                  );
                })}
                <a
                  href="#connexion"
                  className="text-primary hover:text-primary/80 font-medium"
                >
                  Connexion
                </a>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable content */}
      <div className="relative z-10 flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
          <section className="text-center w-full pt-12">
            {content.visibleSections.header && (
              <>
                <h1 className="text-5xl font-bold mb-6 text-gray-900">{content.title}</h1>
                <p className="text-xl mb-12 text-gray-600">{content.subtitle}</p>
              </>
            )}
            
            {content.visibleSections.banner && (
              <div className="relative h-[450px] w-full overflow-hidden rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] group">
                {content.bannerImages.map((url, index) => (
                  <div
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-500 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    <img
                      src={url}
                      alt={`Banner ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}

                <button
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>

                <button
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {content.bannerImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentImageIndex(index)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        index === currentImageIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Bloc de connexion */}
          <section id="connexion" className="bg-white rounded-xl p-8 w-full max-w-lg mx-auto shadow-[0_20px_50px_rgba(0,0,0,0.3)] transition-shadow hover:shadow-[0_25px_60px_rgba(0,0,0,0.4)]">
            <Login onLoginSuccess={() => navigate('/dashboard')} />
          </section>

          <SectionComponent section={content.section1} visible={content.visibleSections.section1} />
          <SectionComponent section={content.section2} visible={content.visibleSections.section2} />
          <SectionComponent section={content.section3} visible={content.visibleSections.section3} />
          <SectionComponent section={content.section4} visible={content.visibleSections.section4} />

          {content.visibleSections.search && (
            <section className="bg-white rounded-xl p-8 w-full shadow-[0_20px_50px_rgba(0,0,0,0.3)] transition-shadow hover:shadow-[0_25px_60px_rgba(0,0,0,0.4)]">
              <div className="max-w-2xl mx-auto">
                <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                  Nombre de formations achevées dans votre structure
                </h2>
                <div className="flex gap-4 mb-6">
                  <input
                    type="text"
                    placeholder="Renseignez votre numéro de client"
                    value={clientNumber}
                    onChange={(e) => setClientNumber(e.target.value)}
                    onKeyPress={handleKeyPress}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || !clientNumber.trim()}
                    className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 flex items-center gap-2"
                  >
                    {isSearching ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <Search className="w-5 h-5" />
                    )}
                    {isSearching ? 'Recherche...' : 'Rechercher'}
                  </button>
                </div>

                {searchError && (
                  <div className="text-red-600 text-center mb-4">
                    {searchError}
                  </div>
                )}

                {structureStats && (
                  <div className="space-y-4">
                    <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                      <h3 className="font-semibold text-gray-900 text-lg mb-2">
                        {structureStats.name}
                      </h3>
                      <p className="text-sm text-gray-500">
                        Client #{structureStats.externalClientId}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Users className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total apprenants</p>
                          <p className="text-xl font-bold text-gray-900">{structureStats.learnersCount}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Formations terminées</p>
                          <p className="text-xl font-bold text-gray-900">{structureStats.completedCount}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-gray-600">Taux de complétion</span>
                        <span className="font-bold text-primary">{structureStats.completionRate}%</span>
                      </div>
                      <div className="w-full h-3 bg-gray-200 rounded-full">
                        <div
                          className="h-3 bg-primary rounded-full transition-all duration-500"
                          style={{ width: `${structureStats.completionRate}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Footer Menu */}
      {content.visibleSections.footer && (
        <footer className="bg-white shadow-[0_-5px_20px_rgba(0,0,0,0.1)] relative z-10 mt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row justify-center items-center gap-6">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex items-center gap-2 text-gray-600">
                  <MapPin className="w-4 h-4 text-primary" />
                  <span>{content?.footer.address}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4 text-primary" />
                  <span>{content?.footer.phone} / {content?.footer.mobile}</span>
                </div>

                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4 text-primary" />
                  <a href={`mailto:${content?.footer.email}`} className="hover:text-primary transition-colors">
                    {content?.footer.email}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}

    </div>
  );
};